/**
 * Browser-side env loader (Vite-injected).
 *
 * Vite already enforces that only `VITE_*` vars reach the bundle. This
 * module adds:
 *   - one typed import surface, so nobody has to remember the exact var
 *     name at call sites,
 *   - explicit presence validation for vars the SPA can't function without
 *     (currently: Supabase URL + anon key),
 *   - dev/prod helpers (`IS_PROD`) for runtime tweaks.
 *
 * Add new public env vars HERE — nowhere else in `src/` should call
 * `import.meta.env.*` directly.
 */

function readVite(name: keyof ImportMetaEnv): string | undefined {
  const value = import.meta.env[name];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function requireVite(name: keyof ImportMetaEnv): string {
  const value = readVite(name);
  if (!value) {
    throw new Error(
      `[env] Missing required env var "${String(name)}". Copy .env.example to ` +
        `.env.local, fill it in, and restart the dev server.`,
    );
  }
  return value;
}

export const IS_PROD = import.meta.env.PROD;
export const IS_DEV = import.meta.env.DEV;
export const MODE = import.meta.env.MODE;

export const env = {
  IS_PROD,
  IS_DEV,
  MODE,

  /** Supabase project URL (e.g. https://abc.supabase.co). Required. */
  SUPABASE_URL: requireVite("VITE_SUPABASE_URL"),
  /** Supabase anon / publishable key. Safe in browser; RLS is the gate. */
  SUPABASE_ANON_KEY: requireVite("VITE_SUPABASE_ANON_KEY"),

  /** Bearer for the demo POS simulator UI. Defaults to "dev-token" when unset. */
  POS_WEBHOOK_TOKEN: readVite("VITE_POS_WEBHOOK_TOKEN") ?? "dev-token",

  /**
   * Explicit WebSocket override. When unset, `floorBus` infers from
   * `window.location` (direct to :8787 in dev, same-origin /ws via reverse
   * proxy in prod).
   */
  WS_URL: readVite("VITE_WS_URL"),

  /**
   * Consumer email-verification UX switch. MUST mirror the Supabase dashboard
   * toggle (Authentication → Sign In / Up → "Confirm email"): when both are on,
   * sign-up shows the check-your-inbox screen (+ resend) instead of an instant
   * session. Flipping verification on later = dashboard toggle + set this to
   * "on" in Netlify + rebuild. No code change.
   */
  AUTH_EMAIL_CONFIRM: readVite("VITE_AUTH_EMAIL_CONFIRM") === "on",

  /**
   * "Continue with Apple" (accounts-auth.md Rule 3). MUST mirror the Supabase
   * Apple provider: on only once Apple is set up there (Services ID, key,
   * return URL), so nobody ever sees a button that fails. Off = no button.
   */
  AUTH_APPLE: readVite("VITE_AUTH_APPLE") === "on",

  /**
   * Cloudflare Turnstile site key (public by design — the SECRET lives only in
   * the Supabase dashboard). MUST mirror the Supabase toggle (Authentication →
   * Bot and Abuse Protection): key set here + toggle off = widget shows, token
   * ignored (harmless soak state); toggle on requires this key in the build or
   * every password sign-in/up fails. Unset ⇒ no widget, no token — captcha
   * fully off. Local dev: Turnstile test keys (1x…AA pass / 2x…AB block).
   */
  TURNSTILE_SITE_KEY: readVite("VITE_TURNSTILE_SITE_KEY"),

  /**
   * Embedding-similarity nudge in the "Recommended for you" strip (It-7, beta).
   * OFF unless this is exactly "on" — and off means the app never calls
   * `similar_venues` at all, not merely that it ignores the answer. Turning it
   * on needs schema §30 pasted AND `scripts/ai/embed-venues.mjs` run; without
   * embeddings the RPC just answers nothing, so a premature flip degrades to
   * today's behavior rather than breaking the strip.
   */
  AI_SIMILARITY: readVite("VITE_AI_SIMILARITY") === "on",
} as const;
